import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col, Badge, Spinner, Alert
} from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

const STREAM_LABELS = {
  general: { label: 'สามัญ', bg: 'primary' },
  religious: { label: 'ศาสนา', bg: 'success' },
  common: { label: 'เก็บทุกคน', bg: 'dark' }
};

const emptyForm = {
  tb_fee_item_item_id: '',
  stream: 'common',
  tb_class_c_id: '',
  tb_program_p_id: '',
  is_class_ic_id: '',
  is_program_id: '',
  amount: ''
};

const FeePlans = () => {
  const location = useLocation();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(location.state?.termId || '');
  const [refs, setRefs] = useState({ classes: [], programs: [], islamicClasses: [], islamicPrograms: [] });
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // initial loads: terms, references, items
  useEffect(() => {
    (async () => {
      try {
        const [termRes, refRes, itemRes] = await Promise.all([
          axiosInstance.get('/api/fee/terms'),
          axiosInstance.get('/api/fee/references'),
          axiosInstance.get('/api/fee/items', { params: { activeOnly: true } })
        ]);
        if (termRes.data.success) {
          setTerms(termRes.data.data);
          if (!selectedTerm && termRes.data.data.length > 0) {
            setSelectedTerm(termRes.data.data[0].term_id);
          }
        }
        if (refRes.data.success) setRefs(refRes.data.data);
        if (itemRes.data.success) setItems(itemRes.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'โหลดข้อมูลตั้งต้นไม่สำเร็จ');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async (termId) => {
    if (!termId) { setPlans([]); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${termId}/plans`);
      if (res.data.success) setPlans(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดตารางราคาไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans(selectedTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  const currentTermLabel = useMemo(() => {
    const t = terms.find((x) => String(x.term_id) === String(selectedTerm));
    return t ? `${t.year_year} / ภาค ${t.semester}` : '';
  }, [terms, selectedTerm]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      tb_fee_item_item_id: plan.tb_fee_item_item_id,
      stream: plan.stream,
      tb_class_c_id: plan.tb_class_c_id || '',
      tb_program_p_id: plan.tb_program_p_id || '',
      is_class_ic_id: plan.is_class_ic_id || '',
      is_program_id: plan.is_program_id || '',
      amount: plan.amount
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.tb_fee_item_item_id) {
      setError('กรุณาเลือกรายการค่าใช้จ่าย');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        tb_fee_term_term_id: selectedTerm,
        tb_fee_item_item_id: form.tb_fee_item_item_id,
        stream: form.stream,
        tb_class_c_id: form.tb_class_c_id || null,
        tb_program_p_id: form.tb_program_p_id || null,
        is_class_ic_id: form.is_class_ic_id || null,
        is_program_id: form.is_program_id || null,
        amount: Number(form.amount) || 0
      };
      if (editing) {
        await axiosInstance.put(`/api/fee/plans/${editing.plan_id}`, payload);
      } else {
        await axiosInstance.post('/api/fee/plans', payload);
      }
      setShowModal(false);
      fetchPlans(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm('ลบรายการราคานี้?')) return;
    try {
      await axiosInstance.delete(`/api/fee/plans/${plan.plan_id}`);
      fetchPlans(selectedTerm);
    } catch (err) {
      setError(err.response?.data?.message || 'ลบไม่สำเร็จ');
    }
  };

  const renderScope = (plan) => {
    if (plan.stream === 'general') {
      return `${plan.c_class || 'ทุกชั้น'}${plan.p_name ? ' · ' + plan.p_name : ''}`;
    }
    if (plan.stream === 'religious') {
      return `${plan.ic_class || 'ทุกชั้น'}${plan.is_programName ? ' · ' + plan.is_programName : ''}`;
    }
    return 'นักเรียนทุกคน';
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">ตั้งราคาตามชั้น / สาย</h2>
              <p className="text-muted mb-0">กำหนดราคาแต่ละรายการ แยกสาย สามัญ / ศาสนา / เก็บทุกคน</p>
            </div>
            <Button variant="primary" onClick={openCreate} disabled={!selectedTerm}>
              <i className="ri-add-line me-1" /> เพิ่มราคา
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>
          )}

          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={5}>
                  <Form.Label>เลือกงวด/ภาคเรียน</Form.Label>
                  <Form.Select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
                    <option value="">-- เลือกงวด --</option>
                    {terms.map((t) => (
                      <option key={t.term_id} value={t.term_id}>
                        {t.year_year} / ภาค {t.semester} {t.term_name ? `(${t.term_name})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                {currentTermLabel && (
                  <Col md="auto">
                    <Badge bg="light" text="dark" className="fs-6">งวดที่เลือก: {currentTermLabel}</Badge>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Body>
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>สาย</th>
                      <th>รายการ</th>
                      <th>ขอบเขต (ชั้น/โปรแกรม)</th>
                      <th className="text-end">ราคา</th>
                      <th className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!selectedTerm ? (
                      <tr><td colSpan={5} className="text-center text-muted py-4">กรุณาเลือกงวด</td></tr>
                    ) : plans.length === 0 ? (
                      <tr><td colSpan={5} className="text-center text-muted py-4">ยังไม่มีรายการราคาในงวดนี้</td></tr>
                    ) : (
                      plans.map((plan) => (
                        <tr key={plan.plan_id}>
                          <td>
                            <Badge bg={STREAM_LABELS[plan.stream]?.bg || 'secondary'}>
                              {STREAM_LABELS[plan.stream]?.label || plan.stream}
                            </Badge>
                          </td>
                          <td>{plan.item_name}</td>
                          <td>{renderScope(plan)}</td>
                          <td className="text-end">{Number(plan.amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(plan)}>
                              <i className="ri-pencil-line" />
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(plan)}>
                              <i className="ri-delete-bin-line" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'แก้ไขราคา' : 'เพิ่มราคา'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label>รายการค่าใช้จ่าย</Form.Label>
              <Form.Select
                value={form.tb_fee_item_item_id}
                onChange={(e) => {
                  const item = items.find((i) => String(i.item_id) === e.target.value);
                  setForm({
                    ...form,
                    tb_fee_item_item_id: e.target.value,
                    amount: item ? item.default_amount : ''
                  });
                }}
              >
                <option value="">-- เลือกรายการ --</option>
                {items.map((i) => (
                  <option key={i.item_id} value={i.item_id}>{i.item_name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12}>
              <Form.Label>สาย</Form.Label>
              <Form.Select
                value={form.stream}
                onChange={(e) => setForm({
                  ...form,
                  stream: e.target.value,
                  tb_class_c_id: '', tb_program_p_id: '', is_class_ic_id: '', is_program_id: ''
                })}
              >
                <option value="common">เก็บทุกคน (เช่น ค่าไฟ/ค่าหอ/ค่ากระดาษสอบ)</option>
                <option value="general">สามัญ</option>
                <option value="religious">ศาสนา</option>
              </Form.Select>
            </Col>

            {form.stream === 'general' && (
              <>
                <Col xs={6}>
                  <Form.Label>ชั้น (สามัญ)</Form.Label>
                  <Form.Select
                    value={form.tb_class_c_id}
                    onChange={(e) => setForm({ ...form, tb_class_c_id: e.target.value })}
                  >
                    <option value="">ทุกชั้น</option>
                    {refs.classes.map((c) => (
                      <option key={c.c_id} value={c.c_id}>{c.c_class}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6}>
                  <Form.Label>โปรแกรม</Form.Label>
                  <Form.Select
                    value={form.tb_program_p_id}
                    onChange={(e) => setForm({ ...form, tb_program_p_id: e.target.value })}
                  >
                    <option value="">ทุกโปรแกรม</option>
                    {refs.programs.map((p) => (
                      <option key={p.p_id} value={p.p_id}>{p.p_name || p.p_code}</option>
                    ))}
                  </Form.Select>
                </Col>
              </>
            )}

            {form.stream === 'religious' && (
              <>
                <Col xs={6}>
                  <Form.Label>ชั้น (ศาสนา)</Form.Label>
                  <Form.Select
                    value={form.is_class_ic_id}
                    onChange={(e) => setForm({ ...form, is_class_ic_id: e.target.value })}
                  >
                    <option value="">ทุกชั้น</option>
                    {refs.islamicClasses.map((c) => (
                      <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={6}>
                  <Form.Label>โปรแกรม (ศาสนา)</Form.Label>
                  <Form.Select
                    value={form.is_program_id}
                    onChange={(e) => setForm({ ...form, is_program_id: e.target.value })}
                  >
                    <option value="">ทุกโปรแกรม</option>
                    {refs.islamicPrograms.map((p) => (
                      <option key={p.is_programId} value={p.is_programId}>
                        {p.is_programName || p.is_programCode}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </>
            )}

            <Col xs={12}>
              <Form.Label>ราคา (บาท)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeePlans;
